import {useState} from 'react';

import {not} from 'ramda';

import {usePostDirectSaleLabourItemToBasketMutation} from '@dms/api/warehouse';
import {handleApiError} from '@dms/shared';

import {ApiError} from 'shared/web';

interface UseAddLabourItemsToBasketParams {
  directSaleId: string;
  /** Called once per batch, only when at least one item made it into the basket. */
  onItemsAdded: VoidFunction;
}

/**
 * Adds labour catalogue items to the direct-sale basket one request at a time,
 * stopping at the first failure so a rejected item cannot be skipped silently.
 */
export function useAddLabourItemsToBasket(params: UseAddLabourItemsToBasketParams) {
  const [addItemToBasket] = usePostDirectSaleLabourItemToBasketMutation();

  const [isAddingItemsToBasket, setIsAddingItemsToBasket] = useState(false);

  const tryAddItemToBasket = (labourCatalogId: string) =>
    addItemToBasket({directSaleId: params.directSaleId, body: {labourCatalogId}})
      .unwrap()
      .then(() => true)
      .catch((error: ApiError) => {
        handleApiError(error);
        return false;
      });

  const addItemsToBasket = async (labourCatalogIds: string[]) => {
    setIsAddingItemsToBasket(true);

    let hasAddedAnyItem = false;

    // Cleanup must be unconditional - handleApiError can itself throw on a malformed error body,
    // and this modal has no close button, so a stuck flag locks the user in the dialog for good.
    try {
      for (const labourCatalogId of labourCatalogIds) {
        const isAdded = await tryAddItemToBasket(labourCatalogId);

        if (not(isAdded)) {
          break;
        }

        hasAddedAnyItem = true;
      }
    } finally {
      if (hasAddedAnyItem) {
        params.onItemsAdded();
      }

      setIsAddingItemsToBasket(false);
    }
  };

  return {addItemsToBasket, isAddingItemsToBasket};
}
